import { View, Text, Pressable, ScrollView, RefreshControl } from 'react-native';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useIsFocused, useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { keepPreviousData } from '@tanstack/react-query';
import { trpc } from '../../lib/trpc';
import { Card, LoadingSpinner, ErrorState, TransactionList, PageContainer, PageHeader, TextInput, Button, WebHead, useConfirmationDialog, CategoryRuleBuilder, CategoryPickerModal } from '../../components';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { useColors } from '../../lib/colors';
import { CATEGORY_GROUP_LABELS } from '../../lib/category-config';

const PERIOD_OPTIONS = [
  { label: 'This Month', descriptor: 'this month', days: 30 },
  { label: 'Last 90 Days', descriptor: 'in the last 90 days', days: 90 },
  { label: 'This Year', descriptor: 'this year', days: 365 },
] as const;

type Period = (typeof PERIOD_OPTIONS)[number];
type SortMode = 'date' | 'amount_high' | 'amount_low';

const SORT_OPTIONS: { value: SortMode; label: string; icon: React.ComponentProps<typeof Ionicons>['name'] }[] = [
  { value: 'date', label: 'Date', icon: 'calendar-outline' },
  { value: 'amount_high', label: 'Highest', icon: 'arrow-up' },
  { value: 'amount_low', label: 'Lowest', icon: 'arrow-down' },
];

/** Account types shown in the filter (spending accounts only). */
const FILTERABLE_ACCOUNT_TYPES = new Set(['depository', 'credit']);

function periodToDateRange(days: number): { startDate: string; endDate: string } {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);
  return {
    startDate: startDate.toISOString().split('T')[0]!,
    endDate: endDate.toISOString().split('T')[0]!,
  };
}

/** Build high-level category filter options from the CATEGORY_GROUP_LABELS map. */
function buildCategoryGroupOptions() {
  const excluded = new Set(['INCOME', 'LOAN_DISBURSEMENTS', 'TRANSFER_IN', 'TRANSFER_OUT']);
  return Object.entries(CATEGORY_GROUP_LABELS)
    .filter(([key]) => !excluded.has(key))
    .map(([key, label]) => ({ value: key, label }));
}

// --- helpers for Set<string> state (avoids re-creating sets on every toggle) ---
function toggleInSet(set: Set<string>, value: string): Set<string> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

export default function TransactionsScreen() {
  const isDesktop = useIsDesktop();
  const isFocused = useIsFocused();
  const c = useColors();
  const router = useRouter();
  const params = useLocalSearchParams<{
    accountId?: string;
    accountName?: string;
    plaidPrimary?: string;
    categoryLabel?: string;
    needsReview?: string;
  }>();

  const [period, setPeriod] = useState<Period>(PERIOD_OPTIONS[0]);
  const [page, setPage] = useState(1);
  const [sortMode, setSortMode] = useState<SortMode>('date');
  const [filterAccountIds, setFilterAccountIds] = useState<Set<string>>(
    () => params.accountId ? new Set([params.accountId]) : new Set(),
  );
  const [filterPlaidPrimaries, setFilterPlaidPrimaries] = useState<Set<string>>(
    () => params.plaidPrimary ? new Set([params.plaidPrimary]) : new Set(),
  );
  const [showFilters, setShowFilters] = useState(!!(params.accountId || params.plaidPrimary));
  const PAGE_SIZE = 50;

  // Advanced filter state
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advMerchant, setAdvMerchant] = useState('');
  const [advNameContains, setAdvNameContains] = useState('');
  const [advAmountMin, setAdvAmountMin] = useState('');
  const [advAmountMax, setAdvAmountMax] = useState('');
  const [advNeedsReview, setAdvNeedsReview] = useState(params.needsReview === '1');
  const [advLowConfidence, setAdvLowConfidence] = useState(false);
  const [advUncategorized, setAdvUncategorized] = useState(false);
  const hasAdvancedFilters = !!(advMerchant || advNameContains || advAmountMin || advAmountMax || advNeedsReview || advLowConfidence || advUncategorized);

  // Bulk recategorize state
  const [bulkCategoryPickerOpen, setBulkCategoryPickerOpen] = useState(false);
  const [ruleBuilderOpen, setRuleBuilderOpen] = useState(false);
  const bulkRecategorizeMutation = trpc.transactions.bulkRecategorize.useMutation();
  const bulkMarkReviewedMutation = trpc.transactions.bulkMarkReviewed.useMutation();
  const categoriesQ = trpc.budget.listCategories.useQuery(undefined, { enabled: isFocused });
  const utils = trpc.useUtils();
  const { confirm } = useConfirmationDialog();

  // Sync URL params into state when navigating from other tabs (screen stays mounted).
  // Reset all other filters so stale state from a previous visit doesn't bleed through.
  useEffect(() => {
    if (params.accountId) {
      setFilterAccountIds(new Set([params.accountId]));
      setFilterPlaidPrimaries(new Set());
    } else if (params.plaidPrimary) {
      setFilterPlaidPrimaries(new Set([params.plaidPrimary]));
      setFilterAccountIds(new Set());
    } else if (params.needsReview === '1') {
      // Deep-linked from dashboard / push notification — focus the user
      // on transactions needing review, but don't disturb account/category filters.
      setAdvNeedsReview(true);
      setPage(1);
      return;
    } else {
      return; // no param-driven navigation, keep existing state
    }
    // Reset all other filters
    setPeriod(PERIOD_OPTIONS[0]);
    setSortMode('date');
    setAdvMerchant('');
    setAdvNameContains('');
    setAdvAmountMin('');
    setAdvAmountMax('');
    setAdvNeedsReview(false);
    setAdvLowConfidence(false);
    setAdvUncategorized(false);
    setAdvancedOpen(false);
    setShowFilters(true);
    setPage(1);
  }, [params.accountId, params.plaidPrimary, params.needsReview]);

  const reviewCountQ = trpc.transactions.reviewCount.useQuery(undefined, { enabled: isFocused });
  const reviewCount = reviewCountQ.data?.count ?? 0;

  const accountsQ = trpc.accounts.list.useQuery(undefined, { enabled: isFocused });
  const accountOptions = useMemo(() => {
    return (accountsQ.data ?? [])
      .filter((a) => FILTERABLE_ACCOUNT_TYPES.has(a.type))
      .map((a) => ({ value: a.id, label: a.name }));
  }, [accountsQ.data]);
  const categoryGroupOptions = useMemo(buildCategoryGroupOptions, []);

  const { startDate, endDate } = periodToDateRange(period.days);
  const sortBy = sortMode === 'date' ? 'date' as const : 'amount' as const;
  const sortOrder = sortMode === 'amount_low' ? 'asc' as const : 'desc' as const;

  const accountIdParam = filterAccountIds.size > 0
    ? (filterAccountIds.size === 1 ? [...filterAccountIds][0]! : [...filterAccountIds])
    : undefined;
  const plaidPrimaryParam = filterPlaidPrimaries.size > 0
    ? (filterPlaidPrimaries.size === 1 ? [...filterPlaidPrimaries][0]! : [...filterPlaidPrimaries])
    : undefined;

  // When the Needs review pill is active, drop the date range so the list
  // matches the all-time reviewCount the user clicked on. The backlog can
  // include transactions older than the selected period (default 30 days).
  const applyDateRange = !advNeedsReview;

  const { data, isLoading, error, refetch } = trpc.transactions.list.useQuery(
    {
      page,
      pageSize: PAGE_SIZE,
      ...(applyDateRange ? { startDate, endDate } : {}),
      sortBy,
      sortOrder,
      ...(accountIdParam ? { accountId: accountIdParam } : {}),
      ...(plaidPrimaryParam ? { plaidPrimary: plaidPrimaryParam } : {}),
      ...(advMerchant ? { merchantName: advMerchant } : {}),
      ...(advNameContains ? { search: advNameContains } : {}),
      ...(advAmountMin ? { amountMin: parseFloat(advAmountMin) } : {}),
      ...(advAmountMax ? { amountMax: parseFloat(advAmountMax) } : {}),
      ...(advNeedsReview ? { needsReview: true as const } : {}),
      ...(advLowConfidence ? { lowConfidence: true as const } : {}),
      ...(advUncategorized ? { uncategorized: true as const } : {}),
    },
    { enabled: isFocused, placeholderData: keepPreviousData },
  );

  const [refreshing, setRefreshing] = useState(false);
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  const transactions = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasMore = page * PAGE_SIZE < total;

  const clearFilters = () => {
    setFilterAccountIds(new Set());
    setFilterPlaidPrimaries(new Set());
    setAdvMerchant('');
    setAdvNameContains('');
    setAdvAmountMin('');
    setAdvAmountMax('');
    setAdvNeedsReview(false);
    setAdvLowConfidence(false);
    setAdvUncategorized(false);
    setPage(1);
  };

  const hasActiveFilters = filterAccountIds.size > 0 || filterPlaidPrimaries.size > 0 || hasAdvancedFilters;

  const handleBulkCategorySelected = useCallback(
    async (categoryId: number | null) => {
      setBulkCategoryPickerOpen(false);
      if (categoryId == null) return;

      const categoryName =
        categoriesQ.data?.find((category) => category.id === categoryId)?.name ?? 'selected category';
      const criteria = {
        budgetCategoryId: categoryId,
        ...(advMerchant ? { merchantName: advMerchant } : {}),
        ...(advNameContains ? { nameContains: advNameContains } : {}),
        ...(advAmountMin ? { amountMin: parseFloat(advAmountMin) } : {}),
        ...(advAmountMax ? { amountMax: parseFloat(advAmountMax) } : {}),
        ...(filterAccountIds.size === 1 ? { accountId: [...filterAccountIds][0]! } : {}),
      };

      const askCreateRule = (createRule: boolean) => {
        bulkRecategorizeMutation.mutateAsync({ ...criteria, createRule }).then(() => {
          utils.transactions.list.invalidate();
          utils.transactions.reviewCount.invalidate();
          refetch();
        });
      };

      void (async () => {
        const choice = await confirm({
          title: `Recategorize ${total} transactions`,
          message: `Set category to "${categoryName}". Also apply to future transactions?`,
          actions: [
            { key: 'cancel', label: 'Cancel', role: 'cancel' },
            { key: 'current', label: 'Just these' },
            { key: 'future', label: 'Future too' },
          ],
        });

        if (choice === 'current') {
          askCreateRule(false);
        } else if (choice === 'future') {
          askCreateRule(true);
        }
      })();
    },
    [advMerchant, advNameContains, advAmountMin, advAmountMax, confirm, filterAccountIds, total, bulkRecategorizeMutation, utils, refetch, categoriesQ.data],
  );

  const handleBulkMarkReviewed = useCallback(async () => {
    const ids = transactions.map((t) => t.id);
    if (ids.length === 0) return;
    await bulkMarkReviewedMutation.mutateAsync({ ids });
    utils.transactions.list.invalidate();
    utils.transactions.reviewCount.invalidate();
    refetch();
  }, [transactions, bulkMarkReviewedMutation, utils, refetch]);

  // Build active filter summary label
  const activeFilterLabels: string[] = [];
  if (filterAccountIds.size > 0) {
    const names = accountOptions.filter((a) => filterAccountIds.has(a.value)).map((a) => a.label);
    if (names.length > 0) activeFilterLabels.push(names.length <= 2 ? names.join(', ') : `${names.length} accounts`);
  }
  if (filterPlaidPrimaries.size > 0) {
    const labels = categoryGroupOptions.filter((g) => filterPlaidPrimaries.has(g.value)).map((g) => g.label);
    if (labels.length > 0) activeFilterLabels.push(labels.length <= 2 ? labels.join(', ') : `${labels.length} categories`);
  }
  if (advLowConfidence) activeFilterLabels.push('Low confidence');
  if (advUncategorized) activeFilterLabels.push('Uncategorized');
  if (advMerchant) activeFilterLabels.push(`Merchant: ${advMerchant}`);
  if (advNameContains) activeFilterLabels.push(`"${advNameContains}"`);
  if (advAmountMin || advAmountMax) {
    activeFilterLabels.push(`$${advAmountMin || '0'}–${advAmountMax || '∞'}`);
  }

  if (isLoading && !data && !refreshing) return <LoadingSpinner />;
  if (error) {
    return (
      <ErrorState
        error={error}
        screen="transactions"
        metadata={{ failedQueries: 'transactions.list' }}
        onRetry={refetch}
      />
    );
  }

  const filterBar = (
    <View className="gap-2">
      {/* Sort + Filter toggle row */}
      <View className="flex-row items-center gap-2">
        {SORT_OPTIONS.map((opt) => (
          <Pressable
            key={opt.value}
            onPress={() => { setSortMode(opt.value); setPage(1); }}
            className={`flex-row items-center gap-1 px-3 py-1.5 rounded-full ${
              sortMode === opt.value ? 'bg-forest-600' : 'bg-gray-100 dark:bg-gray-700'
            }`}
          >
            <Ionicons
              name={opt.icon}
              size={13}
              color={sortMode === opt.value ? '#fff' : c.textSecondary}
            />
            <Text
              className={`text-xs font-medium ${
                sortMode === opt.value ? 'text-white' : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              {opt.label}
            </Text>
          </Pressable>
        ))}

        <View className="flex-1" />

        <Pressable
          onPress={() => setShowFilters((v) => !v)}
          className={`flex-row items-center gap-1 px-3 py-1.5 rounded-full ${
            hasActiveFilters ? 'bg-green-100 dark:bg-green-900/40' : 'bg-gray-100 dark:bg-gray-700'
          }`}
        >
          <Ionicons
            name="funnel-outline"
            size={13}
            color={hasActiveFilters ? c.primary : c.textSecondary}
          />
          <Text
            className={`text-xs font-medium ${
              hasActiveFilters ? 'text-forest-700 dark:text-forest-500' : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            Filter{hasActiveFilters ? ` (${filterAccountIds.size + filterPlaidPrimaries.size})` : ''}
          </Text>
        </Pressable>
      </View>

      {/* Always-visible bulk mark-reviewed when the needs-review shortcut is active */}
      {advNeedsReview && transactions.length > 0 && !(showFilters && advancedOpen) && (
        <Button
          variant="ghost"
          size="sm"
          onPress={handleBulkMarkReviewed}
          loading={bulkMarkReviewedMutation.isPending}
        >
          Mark {transactions.length} visible as reviewed
        </Button>
      )}

      {/* Filter pills */}
      {showFilters && (
        <View className="gap-3">
          {/* Account filter — multi-select */}
          <View className="gap-1.5">
            <Text className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">Account</Text>
            <View className="flex-row flex-wrap gap-1.5">
              {accountOptions.map((a) => {
                const selected = filterAccountIds.has(a.value);
                return (
                  <Pressable
                    key={a.value}
                    onPress={() => { setFilterAccountIds((s) => toggleInSet(s, a.value)); setPage(1); }}
                    className={`px-3 py-1.5 rounded-full ${
                      selected ? 'bg-forest-600' : 'bg-gray-100 dark:bg-gray-700'
                    }`}
                  >
                    <Text
                      className={`text-xs font-medium ${
                        selected ? 'text-white' : 'text-gray-600 dark:text-gray-400'
                      }`}
                      numberOfLines={1}
                    >
                      {a.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          {/* Category group filter — multi-select */}
          <View className="gap-1.5">
            <Text className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">Category</Text>
            <View className="flex-row flex-wrap gap-1.5">
              {categoryGroupOptions.map((g) => {
                const selected = filterPlaidPrimaries.has(g.value);
                return (
                  <Pressable
                    key={g.value}
                    onPress={() => { setFilterPlaidPrimaries((s) => toggleInSet(s, g.value)); setPage(1); }}
                    className={`px-3 py-1.5 rounded-full ${
                      selected ? 'bg-forest-600' : 'bg-gray-100 dark:bg-gray-700'
                    }`}
                  >
                    <Text
                      className={`text-xs font-medium ${
                        selected ? 'text-white' : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {g.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          {/* Advanced filters toggle */}
          <Pressable
            onPress={() => setAdvancedOpen((v) => !v)}
            className="flex-row items-center gap-1 self-start"
          >
            <Ionicons
              name={advancedOpen ? 'chevron-up' : 'chevron-down'}
              size={14}
              color={hasAdvancedFilters ? c.primary : c.textSecondary}
            />
            <Text
              className={`text-xs font-medium ${
                hasAdvancedFilters ? 'text-forest-700 dark:text-forest-500' : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              Advanced{hasAdvancedFilters ? ' (active)' : ''}
            </Text>
          </Pressable>

          {advancedOpen && (
            <View className="gap-2">
              <View className="flex-row gap-2">
                <View className="flex-1">
                  <TextInput
                    label="Merchant"
                    placeholder="Exact merchant name"
                    value={advMerchant}
                    onChangeText={(v) => { setAdvMerchant(v); setPage(1); }}
                    size="sm"
                  />
                </View>
                <View className="flex-1">
                  <TextInput
                    label="Description contains"
                    placeholder="Part of transaction name"
                    value={advNameContains}
                    onChangeText={(v) => { setAdvNameContains(v); setPage(1); }}
                    size="sm"
                  />
                </View>
              </View>
              <View className="flex-row gap-2">
                <View className="flex-1">
                  <TextInput
                    label="Amount min"
                    placeholder="0.00"
                    value={advAmountMin}
                    onChangeText={(v) => { setAdvAmountMin(v); setPage(1); }}
                    keyboardType="decimal-pad"
                    size="sm"
                  />
                </View>
                <View className="flex-1">
                  <TextInput
                    label="Amount max"
                    placeholder="0.00"
                    value={advAmountMax}
                    onChangeText={(v) => { setAdvAmountMax(v); setPage(1); }}
                    keyboardType="decimal-pad"
                    size="sm"
                  />
                </View>
              </View>
              <View className="flex-row gap-2 mt-1">
                <Pressable
                  onPress={() => { setAdvNeedsReview((v) => !v); setPage(1); }}
                  className={`px-3 py-1.5 rounded-full border ${
                    advNeedsReview
                      ? 'bg-forest-600 border-green-600'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <Text className={`text-xs font-medium ${advNeedsReview ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                    Needs review
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => { setAdvLowConfidence((v) => !v); setPage(1); }}
                  className={`px-3 py-1.5 rounded-full border ${
                    advLowConfidence
                      ? 'bg-amber-500 border-amber-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <Text className={`text-xs font-medium ${advLowConfidence ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                    Low confidence
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => { setAdvUncategorized((v) => !v); setPage(1); }}
                  className={`px-3 py-1.5 rounded-full border ${
                    advUncategorized
                      ? 'bg-gray-600 border-gray-600'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <Text className={`text-xs font-medium ${advUncategorized ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                    Uncategorized
                  </Text>
                </Pressable>
              </View>
            </View>
          )}

          {/* Clear all + Actions row */}
          <View className="flex-row items-center gap-3">
            {hasActiveFilters && (
              <Pressable onPress={clearFilters}>
                <Text className="text-xs text-red-500 font-medium">Clear all filters</Text>
              </Pressable>
            )}
            <View className="flex-1" />
            <Pressable
              onPress={() => setRuleBuilderOpen(true)}
              className="flex-row items-center gap-1"
            >
              <Ionicons name="add-circle-outline" size={14} color={c.primary} />
              <Text className="text-xs text-forest-700 dark:text-forest-500 font-medium">Create Rule</Text>
            </Pressable>
            <Pressable
              onPress={() => router.push('/(app)/category-rules')}
              className="flex-row items-center gap-1"
            >
              <Ionicons name="settings-outline" size={14} color={c.textSecondary} />
              <Text className="text-xs text-gray-500 dark:text-gray-400 font-medium">Rules</Text>
            </Pressable>
          </View>

          {/* Bulk action buttons */}
          {advancedOpen && total > 0 && (
            <View className="gap-2">
              {/* Recategorize only when text/amount filters narrow the list */}
              {!!(advMerchant || advNameContains || advAmountMin || advAmountMax) && (
                <Button
                  variant="primary"
                  size="sm"
                  onPress={() => setBulkCategoryPickerOpen(true)}
                  loading={bulkRecategorizeMutation.isPending}
                >
                  Recategorize All ({total})
                </Button>
              )}
              {/* Mark reviewed always available under advanced filters */}
              {transactions.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onPress={handleBulkMarkReviewed}
                  loading={bulkMarkReviewedMutation.isPending}
                >
                  Mark {transactions.length} visible as reviewed
                </Button>
              )}
            </View>
          )}
        </View>
      )}

      {/* Collapsed filter summary chip */}
      {hasActiveFilters && !showFilters && activeFilterLabels.length > 0 && (
        <View className="flex-row items-center gap-2 flex-wrap">
          <View className="flex-row items-center gap-1 bg-green-100 dark:bg-green-900/40 px-3 py-1.5 rounded-full">
            <Ionicons name="funnel" size={11} color={c.primary} />
            <Text className="text-xs font-medium text-forest-700 dark:text-forest-500" numberOfLines={1}>
              {activeFilterLabels.join(' + ')}
            </Text>
            <Pressable onPress={clearFilters} hitSlop={8}>
              <Ionicons name="close-circle" size={14} color={c.primary} />
            </Pressable>
          </View>
        </View>
      )}
    </View>
  );

  const periodPills = (
    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
      <View className="flex-row gap-2">
        {PERIOD_OPTIONS.map((p) => (
          <Pressable
            key={p.label}
            onPress={() => { setPeriod(p); setAdvNeedsReview(false); setPage(1); }}
            className={`px-4 py-2 rounded-full ${
              period.label === p.label ? 'bg-forest-600' : 'bg-gray-100 dark:bg-gray-700'
            }`}
          >
            <Text
              className={`text-sm font-medium ${
                period.label === p.label ? 'text-white' : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              {p.label}
            </Text>
          </Pressable>
        ))}

        {(reviewCount > 0 || advNeedsReview) && (
          <Pressable
            onPress={() => { setAdvNeedsReview((v) => !v); setPage(1); }}
            accessibilityLabel={`${reviewCount} transactions need review`}
            className={`flex-row items-center gap-1 px-4 py-2 rounded-full ${
              advNeedsReview ? 'bg-amber-500' : 'bg-amber-100 dark:bg-amber-900/40'
            }`}
          >
            <Ionicons
              name="alert-circle-outline"
              size={14}
              color={advNeedsReview ? '#fff' : '#b45309'}
            />
            <Text
              className={`text-sm font-medium ${
                advNeedsReview ? 'text-white' : 'text-amber-700 dark:text-amber-300'
              }`}
            >
              {reviewCount}
            </Text>
          </Pressable>
        )}
      </View>
    </ScrollView>
  );

  const summaryText = (
    <Text className="text-sm text-gray-500">
      {advNeedsReview
        ? `${total} transaction${total !== 1 ? 's' : ''} need review`
        : `${total} transaction${total !== 1 ? 's' : ''} ${period.descriptor}`}
    </Text>
  );

  const footer = hasMore ? (
    <Pressable onPress={() => setPage((p) => p + 1)} className="mt-4 items-center py-3">
      <Text className="text-sm text-green-600 font-medium">Load more</Text>
    </Pressable>
  ) : null;

  const bulkCategoryModal = (
    <CategoryPickerModal
      visible={bulkCategoryPickerOpen}
      onClose={() => setBulkCategoryPickerOpen(false)}
      selectedCategoryId={null}
      onSelect={handleBulkCategorySelected}
      title={`Recategorize ${total} transactions`}
      subtitle="Search or browse budget categories"
    />
  );

  if (isDesktop) {
    return (
      <>
        <WebHead><title>Transactions – Per Diem</title></WebHead>
        <ScrollView
          className="flex-1 bg-canvas dark:bg-canvas-dark"
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={c.primary} />
          }
        >
          <PageContainer>
            <PageHeader title="Transactions" />
            <Card className="p-0 overflow-hidden">
              <View className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                {periodPills}
              </View>
              <View className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                {filterBar}
              </View>
              {transactions.length > 0 && (
                <View className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                  {summaryText}
                </View>
              )}
              <TransactionList
                transactions={transactions}
                onUpdated={async () => { await refetch(); utils.transactions.reviewCount.invalidate(); }}
                emptyMessage={`No transactions found for ${period.label.toLowerCase()}.`}
                ListFooterComponent={footer}
              />
            </Card>
          </PageContainer>
        </ScrollView>
        {bulkCategoryModal}
        <CategoryRuleBuilder
          visible={ruleBuilderOpen}
          onClose={() => setRuleBuilderOpen(false)}
          onCreated={async () => {
            await refetch();
            utils.transactions.reviewCount.invalidate();
          }}
        />
      </>
    );
  }

  return (
    <>
      <WebHead><title>Transactions – Per Diem</title></WebHead>
      <View className="flex-1 bg-canvas dark:bg-canvas-dark">
        <View className="bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 px-4 py-3">
          {periodPills}
        </View>
        <View className="bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 px-4 py-3">
          {filterBar}
        </View>
        {transactions.length > 0 && (
          <View className="px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
            {summaryText}
          </View>
        )}
        <TransactionList
          transactions={transactions}
          onUpdated={async () => { await refetch(); utils.transactions.reviewCount.invalidate(); }}
          emptyMessage={`No transactions found for ${period.label.toLowerCase()}.`}
          ListFooterComponent={footer}
        />
      </View>
      {bulkCategoryModal}
      <CategoryRuleBuilder
        visible={ruleBuilderOpen}
        onClose={() => setRuleBuilderOpen(false)}
        onCreated={async () => {
          await refetch();
          utils.transactions.reviewCount.invalidate();
        }}
      />
    </>
  );
}
